import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import { fetchDailyTherapyActivities } from '../api/therapiesApi';
import DailyActivitiesTable from '../components/DailyActivitiesTable';
import { useAuth } from '../../../context/useAuth';
import { PAGE_SIZE } from '../../../config';
import { isStaff } from '../../../shared/utils/roles';

const DEFAULT_LIMIT = PAGE_SIZE;
const activityTypeOptions = [
  { value: 'created', label: 'Dodata terapija' },
  { value: 'updated', label: 'Izmenjena terapija' },
  { value: 'renewed', label: 'Obnovljena terapija' },
  { value: 'deleted', label: 'Obrisana terapija' },
  {
    value: 'status_changed',
    label: 'Promenjen status aktivnosti',
  },
  { value: 'therapy_terminated', label: 'Prekinuta terapija' },
  { value: 'reservation_created', label: 'Kreirana rezervacija' },
  { value: 'reservation_updated', label: 'Izmenjena rezervacija' },
  { value: 'reservation_deleted', label: 'Obrisana rezervacija' },
  { value: 'reservation_applied', label: 'Primenjena rezervacija' },
  { value: 'request_created', label: 'Kreiran zahtev' },
  { value: 'request_updated', label: 'Izmenjen zahtev' },
  { value: 'request_sent', label: 'Poslat zahtev' },
  { value: 'request_cancelled', label: 'Otkazan zahtev' },
];

export default function DailyActivitiesPage() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');
  const latestActivitiesRequestRef = useRef(0);

  const loadActivities = useCallback(async (page = currentPage) => {
    if (!selectedDate) {
      setActivities([]);
      setTotalPages(1);
      setTotalResults(null);
      setListLoading(false);
      return;
    }

    const requestId = latestActivitiesRequestRef.current + 1;
    latestActivitiesRequestRef.current = requestId;
    setListLoading(true);

    const { data, ok, message } = await fetchDailyTherapyActivities({
      date: selectedDate,
      page,
      limit: DEFAULT_LIMIT,
      types:
        selectedActivityTypes.length > 0
          ? selectedActivityTypes
          : activityTypeOptions.map(({ value }) => value),
    });

    if (requestId !== latestActivitiesRequestRef.current) {
      return;
    }

    setListLoading(false);

    if (ok && data) {
      const resolvedPage = data?.pagination?.currentPage || 1;
      // Najnovije ka najstarijoj (po trenutku izmene).
      const sortedActivities = [...(data.activities || [])].sort(
        (left, right) =>
          new Date(right?.changedAt || 0).getTime() -
          new Date(left?.changedAt || 0).getTime()
      );

      setActivities(sortedActivities);
      if (resolvedPage !== page) {
        setCurrentPage(resolvedPage);
      }
      setTotalPages(data?.pagination?.totalPages || 1);
      setTotalResults(data?.pagination?.totalCount ?? null);
      return;
    }

    if (message) {
      setMessage(message);
    }
  }, [currentPage, selectedActivityTypes, selectedDate]);

  useEffect(() => {
    loadActivities(currentPage);
  }, [loadActivities]);

  const handleDateChange = useCallback((nextDate) => {
    setSelectedDate(nextDate || dayjs().format('YYYY-MM-DD'));
    setCurrentPage(1);
  }, []);

  const handleActivityTypesChange = useCallback((nextTypes) => {
    setSelectedActivityTypes(nextTypes);
    setCurrentPage(1);
  }, []);

  const activityTypeFilter = useMemo(
    () => ({
      type: 'custom',
      wide: true,
      value: selectedActivityTypes,
      render: () => (
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          options={activityTypeOptions}
          placeholder="Tip aktivnosti"
          value={selectedActivityTypes}
          onChange={handleActivityTypesChange}
          style={{ width: '100%' }}
        />
      ),
    }),
    [handleActivityTypesChange, selectedActivityTypes]
  );

  const emptyState = useMemo(
    () => ({
      icon: '/assets/icons/no-users.svg',
      title: 'Trenutno nema aktivnosti za izabrani datum',
    }),
    []
  );

  if (!isStaff(user)) {
    return <Navigate to="/neovlascen" replace />;
  }

  return (
    <>
      <ContentLayout>
        <TableSection
          // Za loader tabele (da li jos nema redova).
          data={activities}
          showFilter
          showCreateButton={false}
          searchFieldOrder={['secondarySearch', 'tertiarySearch']}
          onSearch={() => {}}
          filter={{ selected: {}, sections: [] }}
          onFilterChange={() => {}}
          secondarySearch={{
            type: 'date',
            value: selectedDate,
            placeholder: 'Datum aktivnosti',
            onChange: handleDateChange,
          }}
          tertiarySearch={activityTypeFilter}
          customTable={
            <DailyActivitiesTable data={activities} emptyState={emptyState} />
          }
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
        />
      </ContentLayout>
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
